import { Injectable } from '@angular/core';

interface WfToastTimer {
  id: number;
  timeoutId?: ReturnType<typeof setTimeout>;
  remainingTime: number;
  startTime: number;
  callback: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class WfToastTimerService {
  private readonly timers = new Map<number, WfToastTimer>();
  private isPaused = false;

  /**
   * Start a timer for a toast
   */
  start(toastId: number, duration: number, callback: () => void): void {
    // Clear existing timer if any
    this.clear(toastId);

    const timer: WfToastTimer = {
      id: toastId,
      remainingTime: duration,
      startTime: Date.now(),
      callback,
      timeoutId: setTimeout(() => {
        this.clear(toastId);
        callback();
      }, duration),
    };
    this.timers.set(toastId, timer);
  }

  /**
   * Clear a specific timer
   */
  clear(toastId: number): void {
    const timer = this.timers.get(toastId);
    if (timer?.timeoutId) {
      clearTimeout(timer.timeoutId);
    }
    this.timers.delete(toastId);
  }

  /**
   * Clear all timers
   */
  clearAll(): void {
    for (const id of this.timers.keys()) {
      this.clear(id);
    }
  }

  /**
   * Pause all timers
   */
  pause(): void {
    if (this.isPaused) return;
    this.isPaused = true;

    for (const timer of this.timers.values()) {
      if (timer.timeoutId) {
        clearTimeout(timer.timeoutId);
        timer.timeoutId = undefined;
        const elapsed = Date.now() - timer.startTime;
        timer.remainingTime = Math.max(0, timer.remainingTime - elapsed);
      }
    }
  }

  /**
   * Resume all timers
   */
  resume(): void {
    if (!this.isPaused) return;
    this.isPaused = false;

    for (const timer of this.timers.values()) {
      if (timer.remainingTime > 0) {
        timer.startTime = Date.now();
        timer.timeoutId = setTimeout(() => {
          this.clear(timer.id);
          timer.callback();
        }, timer.remainingTime);
      }
    }
  }
}
